import React, { useEffect, useState } from 'react';
import Navbar from '../layout/Navbar';
import { SALES_ORDER_PENDING_PLANS, SALES_ORDER_CLOSE_PLANS, SALES_ORDER_CUSTOMER_LIST } from '../../config/api';
import { decryptData } from '../../utils/encrypt';

function SOPlanningClose() {
  const [userId, setUserId] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [plans, setPlans] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    customer_id: '',
    delivery_from: '',
    delivery_to: '',
    status: 'all',
    plan_status: 'all'
  });

  useEffect(() => {
    const stored = localStorage.getItem('suyog_user');

    const today = new Date();
    const toDate = new Date(today);
    const fromDate = new Date(today);
    fromDate.setDate(fromDate.getDate() - 15);

    const format = (d) => d.toISOString().split('T')[0];

    const defaultFilters = {
      customer_id: '',
      delivery_from: format(fromDate),
      delivery_to: format(toDate),
      status: 'all',
      plan_status: 'all'
    };

    setFilters(defaultFilters);

    if (stored) {
      const parsed = decryptData(stored);
      const uid = parsed.user_id;
      const oid = parsed?.selected_org?.value || null;

      setUserId(uid);
      setOrgId(oid);
      fetchPlans(uid, oid, defaultFilters);
      fetchCustomers(uid);
    }
  }, []);

  const fetchCustomers = async (user_id) => {
    try {
      const res = await fetch(SALES_ORDER_CUSTOMER_LIST, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id })
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setCustomerOptions(data);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchPlans = async (user_id, org_id, appliedFilters = filters) => {
    setLoading(true);
    try {
      const res = await fetch(SALES_ORDER_PENDING_PLANS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id, org_id, ...appliedFilters })
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setPlans(data);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckbox = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const updatePlanStatus = async (status) => {
    if (selectedIds.length === 0) {
      alert(`Please select at least one plan to ${status === 0 ? 'close' : 're-open'}.`);
      return;
    }

    const confirm = window.confirm(`Are you sure you want to ${status === 0 ? 'close' : 're-open'} ${selectedIds.length} plan(s)?`);
    if (!confirm) return;

    try {
      const res = await fetch(SALES_ORDER_CLOSE_PLANS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          org_id: orgId,
          plan_ids: selectedIds,
          status: status
        })
      });

      const data = await res.json();
      if (data.success) {
        alert(`Successfully updated ${data.success} plan(s)`);
        setSelectedIds([]);
        fetchPlans(userId, orgId, filters);
      } else {
        alert('Failed to update plans.');
      }
    } catch (err) {
      console.error('Error updating plans:', err);
      alert('Server error.');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    if (userId && orgId) {
      fetchPlans(userId, orgId, filters);
    }
  };

  const selectedPlans = plans.filter(p => selectedIds.includes(p.id));
  const selectedStatuses = selectedPlans.map(p => p.status);
  const uniqueStatuses = [...new Set(selectedStatuses)];
  const hasMixedStatus = uniqueStatuses.length > 1;

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <div className="card border rounded-4 shadow-sm">
          <div className="card-body p-3">
            <h5 className="mb-3 text-primary">Close SO Planning</h5>

            <div className="row g-2 mb-3 small">
              <div className="col-md-3">
                <label className="form-label">Customer</label>
                <select name="customer_id" value={filters.customer_id} onChange={handleFilterChange} className="form-select">
                  <option value="">-- All Customers --</option>
                  {customerOptions.map(c => (
                    <option key={c.id} value={c.id}>{c.account_name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Delivery From</label>
                <input type="date" name="delivery_from" className="form-control" value={filters.delivery_from} onChange={handleFilterChange} />
              </div>
              <div className="col-md-2">
                <label className="form-label">Delivery To</label>
                <input type="date" name="delivery_to" className="form-control" value={filters.delivery_to} onChange={handleFilterChange} />
              </div>
              <div className="col-md-2">
                <label className="form-label">Status</label>
                <select name="status" className="form-select" value={filters.status} onChange={handleFilterChange}>
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="partial">Partially Invoiced</option>
                  <option value="full">Fully Invoiced</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Plan Status</label>
                <select name="plan_status" className="form-select" value={filters.plan_status} onChange={handleFilterChange}>
                  <option value="all">All</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div className="col-md-1 d-flex align-items-end">
                <button className="btn btn-outline-primary w-100" onClick={applyFilters}>Apply</button>
              </div>
            </div>

            {loading ? (
              <div className="text-muted">Loading plans...</div>
            ) : plans.length === 0 ? (
              <div className="text-muted">No matching plans found.</div>
            ) : (
              <>
                <table className="table table-sm table-bordered text-nowrap small align-middle">
                  <thead className="table-light">
                    <tr>
                      <th></th>
                      <th>Customer</th>
                      <th>Material</th>
                      <th>Delivery</th>
                      <th className="text-end">Planned Qty</th>
                      <th className="text-end">Invoiced Qty</th>
                      <th>Status</th>
                      <th>Invoice Date</th>
                      <th>Destination</th>
                      <th>Plan Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((plan) => {
                      const planned = Number(plan.planned_qty || 0);
                      const invoiced = Number(plan.invoiced_qty || 0);
                      const isClosed = plan.status === 0;
                      const isDisabled = invoiced === 0;

                      const disableThisCheckbox =
                        selectedIds.length > 0 &&
                        ((uniqueStatuses.includes(0) && !isClosed) ||
                        (uniqueStatuses.includes(1) && isClosed));

                      let statusText = 'Pending';
                      let statusClass = 'secondary';

                      if (invoiced >= planned && planned > 0) {
                        statusText = 'Fully Invoiced';
                        statusClass = 'success';
                      } else if (invoiced > 0) {
                        statusText = 'Partially Invoiced';
                        statusClass = 'warning';
                      }

                      return (
                        <tr key={plan.id} className={selectedIds.includes(plan.id) ? 'table-warning' : ''}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(plan.id)}
                              onChange={() => handleCheckbox(plan.id)}
                              disabled={isDisabled || disableThisCheckbox}
                            />
                          </td>
                          <td>{plan.account_name}</td>
                          <td>{plan.material_name}</td>
                          <td>{plan.delivery_date}</td>
                          <td className="text-end">{planned.toFixed(3)}</td>
                          <td className="text-end text-muted">{invoiced.toFixed(3)}</td>
                          <td><span className={`badge bg-${statusClass}`}>{statusText}</span></td>
                          <td>{plan.latest_invoice_date}</td>
                          <td>{plan.destination}</td>
                          <td>
                            <span className={`badge ${isClosed ? 'bg-dark' : 'bg-success'}`}>{isClosed ? 'Closed' : 'Open'}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-danger mt-2"
                    onClick={() => updatePlanStatus(0)}
                    disabled={hasMixedStatus || selectedIds.length === 0 || selectedPlans.some(p => p.status === 0)}
                  >
                    Close Selected Plans
                  </button>
                  <button
                    className="btn btn-success mt-2"
                    onClick={() => updatePlanStatus(1)}
                    disabled={hasMixedStatus || selectedIds.length === 0 || selectedPlans.some(p => p.status === 1 || p.status === null)}
                  >
                    Re-Open Selected Plans
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default SOPlanningClose;
